/* Filtrous recently viewed - v1.3, 2026-08-26
   PDP rail of the last products the visitor opened. localStorage only
   (fv3-recently-viewed-v1, 12 stored / 8 rendered) - no app, no metafields,
   works for anonymous sessions. The CURRENT product is captured from the
   catalog index as it loads: this script wraps window.fetch (read-only tap,
   the response passes through untouched) and must load AFTER
   filtrous-catalog-supplement.js and filtrous-live-stock.js so the record it
   snapshots already has spliced products and corrected images; like them it
   is NOT deferred, so the tap is installed before the deferred app requests
   the index. Products absent from the index (or a missed tap) fall back to
   the public /products/<handle>.js after 4s. The rail renders into the
   bottom of .fv3-pdp once the app mounts it (MutationObserver, same pattern
   as the zoom shim), so it disappears with the PDP when the app hash-routes
   to a sheet view and returns when it routes back. Cards show the stored
   snapshot - LIST price at the time of the visit; filtrous-b2b-price.js does
   not touch this rail. SKUs containing OSK render "Call for Quote" (same
   rule as filtrous-cross-sell.liquid).
   v1.1 (same day): PRERENDER GUARD. The theme runs instant.page, which
   prerenders hovered product links via the Speculation Rules API; Chrome
   executes a prerendered document's scripts, so v1 recorded products the
   visitor merely hovered. record() now defers until prerenderingchange
   fires - a prerender never activated never writes.
   v1.2 (same day, Adelaide): cards up 172 -> 224px; ALWAYS-VISIBLE
   scrollbar; hover motion gated on prefers-reduced-motion: no-preference
   (filtrous-animations.js contract); the rail is a labeled keyboard-
   focusable region (filtrous-a11y.js posture).
   v1.3 (same day, Adelaide: "if I click clear, it shouldn't disappear, it
   should just say Nothing to Display"): clearing keeps the section in place
   with an EMPTY STATE line ("Nothing to display yet.") instead of removing
   it mid-read - the section only stays hidden when a pageload starts with
   no history at all (first-ever visit), where an empty box would be noise.
   The Clear control is omitted in the empty state.
   These rail/card styles are deliberately DUPLICATED in
   filtrous-pdp-merch.js (same .fv3-rv-* class names) so either asset can be
   removed without breaking the other - keep the two blocks in sync.
   Additive asset; remove its loader line in sections/filtrous-v3-app.liquid
   to revert. */
(function () {
  'use strict';

  var KEY = 'fv3-recently-viewed-v1';
  var MAX = 12;   // stored
  var SHOW = 8;   // rendered

  function load() {
    try { var a = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(a) ? a : []; }
    catch (e) { return []; }
  }
  function save(a) { try { localStorage.setItem(KEY, JSON.stringify(a)); } catch (e) {} }

  var pdpSku = window.FV3_CONFIG && window.FV3_CONFIG.pdpSku ? String(window.FV3_CONFIG.pdpSku).toUpperCase() : '';

  /* ---- capture the current product into the list ---- */
  var recorded = false;
  function record(item) {
    if (recorded || !item || !item.sku || !item.handle) return;
    if (document.prerendering) {   // instant.page speculation-rules prerender: not a real view yet
      document.addEventListener('prerenderingchange', function () { record(item); }, { once: true });
      return;
    }
    recorded = true;
    var sku = String(item.sku).toUpperCase();
    var a = load().filter(function (e) { return e && e.sku && String(e.sku).toUpperCase() !== sku; });
    a.unshift({ sku: item.sku, handle: item.handle, title: item.title || '', price: item.price, image: item.image || '', ts: Date.now() });
    if (a.length > MAX) a.length = MAX;
    save(a);
    render();
  }

  function fallbackRecord() {
    if (recorded) return;
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m) return;
    fetch('/products/' + m[1] + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j || !j.handle) return;
        var img = j.featured_image || (j.images && j.images[0]) || '';
        if (img.indexOf('//') === 0) img = 'https:' + img;   // product.js URLs are protocol-relative
        record({
          sku: pdpSku || (j.variants && j.variants[0] && j.variants[0].sku) || j.handle,
          handle: j.handle,
          title: j.title,
          price: (j.price / 100).toFixed(2),
          image: img
        });
      })
      .catch(function () {});
  }

  if (pdpSku) {
    var orig = window.fetch;
    if (orig) {
      window.fetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var p = orig.apply(this, arguments);
        if (String(url).indexOf('catalog-index') !== -1) {
          p.then(function (res) {
            if (!res || !res.ok) return;
            return res.clone().json().then(function (data) {
              var list = data && data.products;
              if (!list || !list.length) return;
              for (var i = 0; i < list.length; i++) {
                var it = list[i];
                if (it && it.sku && String(it.sku).toUpperCase() === pdpSku) { record(it); return; }
              }
              fallbackRecord();   // on-page product missing from the index
            });
          }).catch(function () {});
        }
        return p;
      };
    }
    setTimeout(fallbackRecord, 4000);   // index fetch never observed (unexpected)
  }

  /* ---- rail (styles mirrored in filtrous-pdp-merch.js - keep in sync) ---- */
  var css = document.createElement('style');
  css.textContent =
    '#fv3-app .fv3-rv{margin:44px 0 8px;border-top:1px solid var(--line,#e3e9ee);padding-top:22px}' +
    '#fv3-app .fv3-rv-head{display:flex;align-items:baseline;gap:14px;margin:0 0 14px}' +
    '#fv3-app .fv3-rv-head h2{margin:0;font-size:18px;font-weight:700;letter-spacing:-.01em;color:var(--navy,#082f4b)}' +
    '#fv3-app .fv3-rv-clear{margin-left:auto;border:0;background:none;padding:4px 2px;font:inherit;font-size:12.5px;font-weight:600;color:var(--mut,#5b7284);cursor:pointer}' +
    '#fv3-app .fv3-rv-clear:hover{color:var(--dteal,#0d7d68);text-decoration:underline}' +
    '#fv3-app .fv3-rv-empty{margin:2px 0 10px;font-size:13.5px;color:var(--mut,#5b7284)}' +
    '#fv3-app .fv3-rv-rail{display:grid;grid-auto-flow:column;grid-auto-columns:224px;gap:14px;overflow-x:auto;padding:2px 2px 10px;-webkit-overflow-scrolling:touch}' +
    /* always-visible scrollbar: scrollbar-color opts Chrome/Firefox out of
       overlay auto-hide; the webkit pseudos cover older Safari */
    '#fv3-app .fv3-rv-rail{scrollbar-width:thin;scrollbar-color:#b9c6cf #eef2f5}' +
    '#fv3-app .fv3-rv-rail::-webkit-scrollbar{height:8px}' +
    '#fv3-app .fv3-rv-rail::-webkit-scrollbar-track{background:#eef2f5;border-radius:4px}' +
    '#fv3-app .fv3-rv-rail::-webkit-scrollbar-thumb{background:#b9c6cf;border-radius:4px}' +
    '#fv3-app .fv3-rv-rail:focus-visible{outline:2px solid var(--dteal,#0d7d68);outline-offset:2px}' +
    '#fv3-app .fv3-rv-card{display:flex;flex-direction:column;background:#fff;border:1px solid var(--line,#e3e9ee);border-radius:10px;padding:12px;color:inherit;text-decoration:none}' +
    '#fv3-app .fv3-rv-card:hover{border-color:rgba(33,214,179,.45);box-shadow:0 4px 16px rgba(8,47,75,.08)}' +
    /* motion only when the visitor allows it (filtrous-animations.js contract) */
    '@media (prefers-reduced-motion: no-preference){' +
      '#fv3-app .fv3-rv-card{transition:border-color .15s,box-shadow .15s,transform .15s}' +
      '#fv3-app .fv3-rv-card:hover{transform:translateY(-2px)}' +
    '}' +
    '#fv3-app .fv3-rv-img{aspect-ratio:1/1;display:flex;align-items:center;justify-content:center;background:#fff;border-radius:6px;overflow:hidden;margin-bottom:9px}' +
    '#fv3-app .fv3-rv-img img{max-width:100%;max-height:100%;object-fit:contain}' +
    '#fv3-app .fv3-rv-img:empty{background:var(--panel,#f4f6f8)}' +
    '#fv3-app .fv3-rv-t{font-size:13px;font-weight:600;line-height:1.35;color:var(--ink,#082f4b);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;margin-bottom:4px}' +
    '#fv3-app .fv3-rv-sku{font-size:12px;color:var(--mut,#5b7284);margin-bottom:6px}' +
    '#fv3-app .fv3-rv-p{margin-top:auto;font-size:14px;font-weight:700;color:var(--navy,#082f4b)}' +
    '@media (max-width:700px){#fv3-app .fv3-rv{margin-top:34px}#fv3-app .fv3-rv-rail{grid-auto-columns:180px}}';
  document.head.appendChild(css);

  function isQuoteSku(sku) { return String(sku).toUpperCase().indexOf('OSK') !== -1; }
  function fmtPrice(v) {
    var n = typeof v === 'number' ? v : parseFloat(v);
    return isFinite(n) ? '$' + n.toFixed(2) : '';
  }

  function card(e) {
    var a = document.createElement('a');
    a.className = 'fv3-rv-card';
    a.href = '/products/' + e.handle;
    var box = document.createElement('div');
    box.className = 'fv3-rv-img';
    if (e.image) {
      var img = document.createElement('img');
      img.src = e.image;
      img.alt = e.title || '';
      img.loading = 'lazy';
      box.appendChild(img);
    }
    a.appendChild(box);
    var t = document.createElement('div');
    t.className = 'fv3-rv-t';
    t.textContent = e.title || '';
    a.appendChild(t);
    var s = document.createElement('div');
    s.className = 'fv3-rv-sku';
    s.textContent = e.sku;
    a.appendChild(s);
    var p = document.createElement('div');
    p.className = 'fv3-rv-p';
    p.textContent = isQuoteSku(e.sku) ? 'Call for Quote' : fmtPrice(e.price);
    a.appendChild(p);
    return a;
  }

  function head(withClear) {
    var d = document.createElement('div');
    d.className = 'fv3-rv-head';
    var h = document.createElement('h2');
    h.textContent = 'Recently viewed';
    d.appendChild(h);
    if (withClear) {
      var clr = document.createElement('button');
      clr.type = 'button';
      clr.className = 'fv3-rv-clear';
      clr.textContent = 'Clear';
      clr.addEventListener('click', function () { save([]); render(); });
      d.appendChild(clr);
    }
    return d;
  }

  function render() {
    var pdp = document.querySelector('#fv3-app .fv3-pdp');
    if (!pdp) return;
    var items = load().filter(function (e) {
      return e && e.sku && e.handle && String(e.sku).toUpperCase() !== pdpSku;
    }).slice(0, SHOW);
    var host = pdp.querySelector('.fv3-rv');
    if (!items.length) {
      // v1.3: an ALREADY-VISIBLE section shows the empty state instead of
      // vanishing (Adelaide); a pageload with no history at all stays hidden.
      if (!host) return;
      if (host.getAttribute('data-sig') === 'empty') return;
      host.setAttribute('data-sig', 'empty');
      host.innerHTML = '';
      host.appendChild(head(false));
      var p = document.createElement('p');
      p.className = 'fv3-rv-empty';
      p.textContent = 'Nothing to display yet — products you view will show up here.';
      host.appendChild(p);
      return;
    }
    var sig = items.map(function (e) { return e.sku; }).join('|');
    if (host && host.getAttribute('data-sig') === sig) return;
    if (!host) {
      host = document.createElement('section');
      host.className = 'fv3-rv';
      host.setAttribute('aria-label', 'Recently viewed products');
      pdp.appendChild(host);
    }
    host.setAttribute('data-sig', sig);
    host.innerHTML = '';
    host.appendChild(head(true));
    var rail = document.createElement('div');
    rail.className = 'fv3-rv-rail';
    rail.setAttribute('role', 'region');
    rail.setAttribute('aria-label', 'Recently viewed');
    rail.setAttribute('tabindex', '0');
    items.forEach(function (e) { rail.appendChild(card(e)); });
    host.appendChild(rail);
  }

  function boot() {
    render();
    new MutationObserver(render)
      .observe(document.getElementById('fv3-app') || document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
